import { css, cx } from "@emotion/css";
import { ChevronDownIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import React from "react";
import { resetStyles } from "../../../utils";
import { useDropdownButton } from "../../Dropdown/useDropdownButton";
import type { PopoverBasicHelpProps } from "../../Popover/PopoverBasicHelp";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import InputLabel from "../Primitives/InputLabel";
import { Listbox } from "../Primitives/Listbox";
import { inputDisabledStyles, inputErrorStyles, inputReadOnlyStyles, inputStyles } from "../Primitives/SharedFormStyling.styles";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
export interface SelectOption {
    label: string;
    value: string;
}
export interface SelectProps {
    /**
     * The options for the select
     */
    options: SelectOption[];
    /**
     * The label for the select
     */
    label: string;
    /**
     * The current value of the select
     */
    value: string;
    /**
     * The placeholder text to display when input is empty
     */
    placeholder?: string;
    /**
     * The description text to display below the input
     */
    description?: string | DescriptionContent;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether to show optional marker
     */
    hasOptionalMarker?: boolean;
    /**
     * Whether this field has a default value marker
     */
    hasDefaultMarker?: boolean;
    /**
     * PopoverBasicHelp component to display additional help information
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * Whether to autofocus the input
     */
    autoFocus?: boolean;
    /**
     * Whether the input is readonly
     */
    readOnly?: boolean;
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: string) => void;
}
export function Select({ options, label, value, placeholder, description, error, disabled, hasRequiredMarker, hasOptionalMarker, hasDefaultMarker, popover, autoFocus, readOnly, onChange }: SelectProps) {
    const { isOpen, closeDropdown, setButtonRef, toggleDropdown, setDropdownRef, dropdownAttributes, buttonAriaAttributes } = useDropdownButton({ trapFocus: false, dropdownAriaRole: "listbox" });
    const baseId = React.useId();
    const labelId = `label-${baseId}`;
    const selectId = `select-${baseId}`;
    const selectedOptionIndex = options.findIndex((o) => o.value === value);
    const selectedOption = selectedOptionIndex >= 0 ? options[selectedOptionIndex] : undefined;
    const getOptionId = (index: number) => `${dropdownAttributes.dropdownId}-option-${index}`;
    const [activeDescendantId, setActiveDescendantId] = React.useState<string | undefined>(undefined);
    const handleOptionSelected = (option: SelectOption) => {
        onChange(option.value);
        closeDropdown({ moveFocusToButton: true });
    };
    const handleOptionActive = (index: number) => {
        setActiveDescendantId(getOptionId(index));
    };
    const handleSelectKeyDown = (event: React.KeyboardEvent) => {
        const { key } = event;
        if (key === "ArrowDown" || key === "Enter" || key === " ") {
            event.preventDefault();
            toggleDropdown();
        }
    };
    const handleTabPressed = (event: React.KeyboardEvent) => {
        event.preventDefault();
        closeDropdown({ moveFocusToButton: true });
    };
    return (<div className={containerStyles}>
            <div>
                <InputLabel id={labelId} label={label} htmlFor={selectId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${selectId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <div>
                <button id={selectId} ref={setButtonRef} role="combobox" onClick={!disabled && !readOnly ? toggleDropdown : undefined} onKeyDown={handleSelectKeyDown} aria-activedescendant={isOpen ? activeDescendantId : undefined} 
    // This aria-labelledby and aria-label appear redundant but result in correct screen reader announcement when changing options in chromium
    aria-labelledby={labelId} aria-label={selectedOption ? selectedOption.label : undefined} aria-readonly={readOnly} aria-required={hasRequiredMarker} tabIndex={0} autoFocus={autoFocus} className={cx(selectStyles.root, inputStyles, {
            [inputErrorStyles]: !!error,
            [inputDisabledStyles]: disabled,
            [inputReadOnlyStyles]: readOnly,
            [selectStyles.readOnly]: readOnly,
        })} aria-disabled={disabled} aria-invalid={!!error} aria-describedby={[description ? `${selectId}-description` : null, error ? `${selectId}-error` : null].filter(Boolean).join(" ") || undefined} {...buttonAriaAttributes}>
                    {selectedOption ? <span className={selectStyles.value}>{selectedOption.label}</span> : <span className={selectStyles.placeholder}>{placeholder}</span>}
                    <span className={cx(selectStyles.chevronIcon, { [selectStyles.rotatedChevronIcon]: isOpen })}>
                        <ChevronDownIcon size={16}/>
                    </span>
                </button>

                {isOpen && (<div className={dropdownContainerStyles}>
                        <Listbox ref={setDropdownRef} id={dropdownAttributes.dropdownId} value={value} options={options} onOptionSelected={handleOptionSelected} getOptionId={getOptionId} accessibleName={label} onOptionActive={handleOptionActive} onTabPressed={handleTabPressed}/>
                    </div>)}
            </div>
            {error && <InputError error={error} id={`${selectId}-error`}/>}
        </div>);
}
const containerStyles = css({
    display: "grid",
    position: "relative",
    gap: space[8],
    width: "100%",
    maxWidth: "600px",
});
const dropdownContainerStyles = css({
    position: "absolute",
    marginTop: space[8],
    zIndex: 1,
    width: "100%",
});
const selectStyles = {
    root: css({
        ...resetStyles.button,
        display: "flex",
        alignItems: "center",
        gap: space[6],
        minHeight: "2rem",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    }),
    value: css({
        flex: 1,
        color: themeTokens.color.text.primary,
    }),
    placeholder: css({
        flex: 1,
        color: themeTokens.color.text.tertiary,
    }),
    readOnly: css({
        cursor: "unset",
    }),
    chevronIcon: css({
        transition: "transform 150ms ease-in-out",
        padding: `${space[2]} ${space[4]}`,
    }),
    rotatedChevronIcon: css({
        transform: "rotate(-180deg)",
    }),
};
